import { useEffect, useState } from 'react';

/**
 * true когда виртуальная клавиатура открыта.
 *
 * Сигналов два, любого достаточно:
 *  1) focus на input/textarea/contenteditable (надёжнее всего — keyboard всегда открывается на focus)
 *  2) visualViewport.height сильно меньше window.innerHeight (fallback для не-input focus)
 *
 * В Telegram WebApp на iOS viewport ужимается специфически и threshold-метод одна
 * не срабатывает — поэтому focus-сигнал основной.
 */
export function useKeyboardOpen(threshold = 120): boolean {
  const [focused, setFocused] = useState(false);
  const [viewportShrunk, setViewportShrunk] = useState(false);

  useEffect(() => {
    const isTextInput = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === 'TEXTAREA') return true;
      if (tag === 'INPUT') {
        const type = (el as HTMLInputElement).type;
        return ['text', 'number', 'email', 'tel', 'url', 'search', 'password', ''].includes(type);
      }
      if (el.isContentEditable) return true;
      return false;
    };
    const onFocusIn = (e: FocusEvent) => {
      if (isTextInput(e.target)) setFocused(true);
    };
    const onFocusOut = () => {
      // setTimeout чтобы дать новому focus подняться раньше, чем мы выключаем
      setTimeout(() => {
        const active = document.activeElement;
        setFocused(isTextInput(active));
      }, 0);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const update = () => {
      setViewportShrunk(window.innerHeight - vv.height > threshold);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [threshold]);

  return focused || viewportShrunk;
}
